import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import type { Referrals } from '../store/insights'
import { fmt, formatHighlightNumber, type SortDir } from '../utils/insightsUtils'
import { InsightsSection, SortIcon, NumericTrendBadge, MeetingTypeName } from './insightsCommon'
import { type InsightsTrackingClient, NoopInsightsTrackingClient } from '../utils/tracking'

const PartialDataTag: React.FC = () => (
  <div className='insights-match-tag'>
    <i className='bi bi-exclamation-circle' />
    <span>Partial data</span>
  </div>
)

const DEFAULT_VISIBLE_ROWS = 5

type ReferralSortKey = 'meetingType' | 'meetings' | 'referralsAsked' | 'referralsProvided'

const ReferralSortableHeader: React.FC<{
  label: string
  sortKey: ReferralSortKey
  activeKey: ReferralSortKey
  activeDir: SortDir
  onSort: (key: ReferralSortKey) => void
  isName?: boolean
}> = ({ label, sortKey, activeKey, activeDir, onSort, isName }) => {
  const isActive = sortKey === activeKey
  const ariaSort: 'ascending' | 'descending' | 'none' = isActive ? (activeDir === 'asc' ? 'ascending' : 'descending') : 'none'

  return (
    <button
      type='button'
      className={classNames('insights-referrals__table-cell', 'insights-sortable-th', { 'insights-referrals__table-cell--name': isName })}
      onClick={() => onSort(sortKey)}
      role='columnheader'
      aria-sort={ariaSort}
    >
      <div className='insights-sortable-th-inner'>
        <span>{label}</span>
        {isActive && <SortIcon dir={activeDir} />}
      </div>
    </button>
  )
}

export const ReferralsSection: React.FC<{ data: Referrals; trackingClient?: InsightsTrackingClient }> = ({
  data,
  trackingClient = NoopInsightsTrackingClient,
}) => {
  const { metrics, rows, isPartialData } = data
  const [sortKey, setSortKey] = useState<ReferralSortKey>('referralsAsked')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [expanded, setExpanded] = useState(false)

  const onSort = (key: ReferralSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'meetingType' ? 'asc' : 'desc')
    }
    trackingClient.track('sort_changed', { section: 'referrals', column: key })
  }

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      let cmp: number
      if (sortKey === 'meetingType') {
        cmp = a.meetingType.localeCompare(b.meetingType)
      } else {
        cmp = a[sortKey] - b[sortKey]
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [rows, sortKey, sortDir])

  const visibleRows = expanded ? sortedRows : sortedRows.slice(0, DEFAULT_VISIBLE_ROWS)
  const hasMore = sortedRows.length > DEFAULT_VISIBLE_ROWS

  return (
    <InsightsSection
      className='insights-section--compact-gap'
      title='Referrals'
      subtitle='See where referrals are coming from and where to ask more'
      headerRight={isPartialData ? <PartialDataTag /> : undefined}
    >
      <div className='insights-referrals__metrics'>
        <div className='insights-referrals__metric'>
          <div className='insights-asset-opp__metric-bar insights-asset-opp__metric-bar--1' />
          <div className='insights-asset-opp__metric-content'>
            <div className='insights-asset-opp__metric-label'>Referrals asked</div>
            <div className='insights-asset-opp__metric-value-row'>
              <span className='insights-asset-opp__metric-value'>{formatHighlightNumber(metrics.referralsAsked)}</span>
              <NumericTrendBadge value={metrics.referralsAskedTrend} />
            </div>
            <div className='insights-asset-opp__metric-detail'>
              <strong>
                <MeetingTypeName name={metrics.referralsAskedLeadMeetingType} />
              </strong>{' '}
              lead in referral asks
            </div>
          </div>
        </div>

        <div className='insights-referrals__metric'>
          <div className='insights-asset-opp__metric-bar insights-asset-opp__metric-bar--2' />
          <div className='insights-asset-opp__metric-content'>
            <div className='insights-asset-opp__metric-label'>Referrals provided</div>
            <div className='insights-asset-opp__metric-value-row'>
              <span className='insights-asset-opp__metric-value'>{formatHighlightNumber(metrics.referralsProvided)}</span>
              <NumericTrendBadge value={metrics.referralsProvidedTrend} />
            </div>
            <div className='insights-asset-opp__metric-detail'>
              <strong>
                <MeetingTypeName name={metrics.referralsProvidedLeadMeetingType} />
              </strong>{' '}
              lead in referrals provided
            </div>
          </div>
        </div>
      </div>

      {sortedRows.length > 0 && (
        <>
          <div className='insights-referrals__table'>
            <div className='insights-referrals__table-header'>
              <ReferralSortableHeader label='Meeting type' sortKey='meetingType' activeKey={sortKey} activeDir={sortDir} onSort={onSort} isName />
              <ReferralSortableHeader label='Meetings' sortKey='meetings' activeKey={sortKey} activeDir={sortDir} onSort={onSort} />
              <ReferralSortableHeader label='Referrals asked' sortKey='referralsAsked' activeKey={sortKey} activeDir={sortDir} onSort={onSort} />
              <ReferralSortableHeader label='Referrals provided' sortKey='referralsProvided' activeKey={sortKey} activeDir={sortDir} onSort={onSort} />
            </div>
            {visibleRows.map((row, i) => (
              <div
                key={row.meetingType}
                className={classNames('insights-referrals__table-row', { 'insights-referrals__table-row--last': i === visibleRows.length - 1 })}
              >
                <div className='insights-referrals__table-cell insights-referrals__table-cell--name fw-bold'>
                  <MeetingTypeName name={row.meetingType} />
                </div>
                <div className='insights-referrals__table-cell'>
                  <span className='fw-medium'>{fmt(row.meetings)}</span>
                </div>
                <div className='insights-referrals__table-cell'>
                  <span className='fw-medium'>{fmt(row.referralsAsked)}</span>
                </div>
                <div className='insights-referrals__table-cell'>
                  <span className='fw-medium'>{fmt(row.referralsProvided)}</span>
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className='text-center mt-3'>
              <button type='button' className='btn btn-link p-0 insights-link border-0 bg-transparent' onClick={() => setExpanded((prev) => !prev)}>
                {expanded ? 'Show less' : 'Show all meeting types ↓'}
              </button>
            </div>
          )}

          <div className='insights-referrals__footer'>
            <div className='insights-referrals__footer-badge'>
              <i className='bi bi-info-circle' />
              <span>Only meeting types with referral activity are shown</span>
            </div>
          </div>
        </>
      )}
    </InsightsSection>
  )
}
